import {
  Button,
  FormInput,
  FormRoot,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useEffect } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import type { CustomerGroupDTO } from "../api/client";
import { useCreateCustomerGroup, useUpdateCustomerGroup } from "../api/queries";

const CustomerGroupFormSchema = z.object({
  nameLo: z.string().optional(),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  description: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "ຮູບແບບສີຕ້ອງເປັນ #RRGGBB")
    .optional()
    .or(z.literal("")),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

type CustomerGroupFormValues = z.infer<typeof CustomerGroupFormSchema>;

function toFormValues(group?: CustomerGroupDTO | null): CustomerGroupFormValues {
  if (!group)
    return { nameLo: "", nameTh: "", nameEn: "", description: "", color: "", sortOrder: 0 };
  const n = group.name as { lo?: string; th?: string; en?: string };
  return {
    nameLo: n?.lo ?? "",
    nameTh: n?.th ?? "",
    nameEn: n?.en ?? "",
    description: group.description ?? "",
    color: group.color ?? "",
    sortOrder: group.sortOrder ?? 0,
  };
}

type CustomerGroupFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: CustomerGroupDTO | null;
};

export function CustomerGroupFormDialog({
  open,
  onOpenChange,
  group,
}: CustomerGroupFormDialogProps) {
  const isEdit = !!group;
  const create = useCreateCustomerGroup();
  const update = useUpdateCustomerGroup();

  const methods = RHF.useForm<CustomerGroupFormValues>({
    resolver: zodResolver(
      CustomerGroupFormSchema,
    ) as Resolver<CustomerGroupFormValues>,
    defaultValues: toFormValues(group),
  });

  useEffect(() => {
    if (open) methods.reset(toFormValues(group));
  }, [open, group?.id]);

  const onSubmit = async (vals: CustomerGroupFormValues) => {
    const data = {
      name: {
        lo: vals.nameLo || undefined,
        th: vals.nameTh || undefined,
        en: vals.nameEn || undefined,
      },
      description: vals.description || undefined,
      color: vals.color || undefined,
      sortOrder: vals.sortOrder,
    };

    if (isEdit && group) {
      await toast.promise(update.mutateAsync({ id: group.id, data }), {
        loading: "ກຳລັງບັນທຶກ...",
        success: "ບັນທຶກສຳເລັດ",
        error: "ບັນທຶກລົ້ມເຫຼວ",
      });
    } else {
      await toast.promise(create.mutateAsync(data), {
        loading: "ກຳລັງບັນທຶກ...",
        success: "ສ້າງກຸ່ມລູກຄ້າສຳເລັດ",
        error: "ສ້າງລົ້ມເຫຼວ",
      });
    }
    onOpenChange(false);
    methods.reset();
  };

  const isPending = create.isPending || update.isPending;

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-5">
          <div className="flex items-start justify-between border-b pb-4">
            <h3 className="font-semibold text-lg">
              {isEdit ? "ແກ້ໄຂກຸ່ມລູກຄ້າ" : "ສ້າງກຸ່ມລູກຄ້າ"}
            </h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="ປິດ"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <FormInput name="nameLo" label="ຊື່ (ລາວ)" placeholder="ໃສ່ຊື່ພາສາລາວ" />
          <FormInput name="nameTh" label="ຊື່ (ໄທ)" placeholder="ໃສ່ຊື່ພາສາໄທ" />
          <FormInput name="nameEn" label="ຊື່ (ອັງກິດ)" placeholder="ໃສ່ຊື່ພາສາອັງກິດ" />
          <FormInput
            name="description"
            label="ຄຳອະທິບາຍ"
            placeholder="ຄຳອະທິບາຍກຸ່ມ (ບໍ່ຈຳເປັນ)"
          />
          <FormInput
          type="color"
            name="color"
            label="ສີ (HEX)"
            placeholder="#e2a310"
          />
          <FormInput name="sortOrder" label="ລຳດັບ" type="number" />

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
